import { Translation } from '@/components/foundation/Translation'
import { BlockContainer } from '@/components/ui/BlockContainer'
import { ErrorComponent } from '@/components/ui/Error'
import { GridContainer } from '@/components/ui/Grid/GridContainer'
import { LinePicto } from '@/components/ui/LinePicto'
import { Loading } from '@/components/ui/Loading'
import { useDataAllAreas } from '@/hooks/data/useDataAllAreas'
import { useDataAllLines } from '@/hooks/data/useDataAllLines'
import { useDataAllStops } from '@/hooks/data/useDataAllStops'
import { useDataDisruptions } from '@/hooks/data/useDataDisruptions'
import { useDataLinesGroups } from '@/hooks/data/useDataLinesGroups'
import { useDataPoisByType } from '@/hooks/data/useDataPoisByType'
import { useLoadFullLine } from '@/hooks/load/useLoadFullLine'
import { useLoadLines } from '@/hooks/load/useLoadLines'
import { useBoundary } from '@/hooks/useBoundary'
import { useBuffer } from '@/hooks/useBuffer'
import { useExecutor } from '@/hooks/useExecutor'
import { useLinesThroughAreas } from '@/hooks/useLinesThroughAreas'
import { useMobile } from '@/hooks/useMobile'
import { useModule } from '@/hooks/useModule'
import { useObjectsInGeojson } from '@/hooks/useObjectsInGeojson'
import { useTranslator } from '@/hooks/useTranslator'
import { useDispatch, useSelector, useStore } from '@/stores/hooks'
import { getLine } from '@/stores/lines'
import { loadFullLine } from '@/stores/load/loadFullLine'
import { loadStops } from '@/stores/load/loadStops'
import { setHoveredLine } from '@/stores/temp'
import { distance } from '@/util/distance'
import type { Disruption } from '@common/types/Disruption'
import type { Stop } from '@common/types/Stop'
import { latlngToPoint, type LatLng } from '@lc/libgeo'
import { useObserve } from '@lc/libreact'
import booleanPointInPolygon from '@turf/boolean-point-in-polygon'
import type { MultiPolygon, Point } from 'geojson'
import { useCallback, useMemo, useState } from 'react'
import { Link, useLocation } from 'wouter'
import styles from './LinesGroups.scss'

export function LinesGroups() {
	const { lines, status } = useDataAllLines()
	const { linesGroups, status: linesGroupsStatus } = useDataLinesGroups()
	const module = useModule()
	const mobile = useMobile()
	const { disruptions } = useDataDisruptions({ interval: 600000 })

	const ready = status === 'loaded' && linesGroupsStatus === 'loaded'

	const filteredGroups = useMemo(() => {
		if (!linesGroups) return []

		return linesGroups
			.filter(lg => lg.hidden !== true)
			.map(lg => ({ ...lg, lines: lg.lines.filter(() => true) }))
			.filter(lg => lg.lines.length)
	}, [linesGroups])

	if (!ready) {
		return (
			<BlockContainer className={styles.padding}>
				<Loading />
			</BlockContainer>
		)
	}

	return (
		<section className={styles.containerLines}>
			{filteredGroups.map(lg => (
				<GridContainer
					title={{ title: lg.group, color: 'black' }}
					columns={mobile ? lg.mobileGrid : lg.desktopGrid}
					key={lg.group}
					innerClassName={styles.listLines}
				>
					{lg.lines.map(lineId => {
						const findLine = Object.values(lines).find(
							l => l.id === lineId,
						)
						if (findLine) {
							const filteredDisrutions = disruptions.filter(
								disruption =>
									disruption.impacted_objects.some(
										object => object.id === findLine.id,
									),
							)
							let disruption = filteredDisrutions.find(
								filteredDis =>
									filteredDis.severity?.level === 'blocking',
							)
							if (!disruption) {
								disruption = filteredDisrutions.find(
									filteredDis =>
										filteredDis.severity?.level ===
										'warning',
								)
							}

							if (!disruption) {
								disruption = filteredDisrutions.find(
									filteredDis =>
										filteredDis.severity?.level === 'info',
								)
							}
							return (
								<Link
									className={styles.lineItem}
									href={`${module.path}/${findLine.id}/forward`}
									key={findLine.id}
									aria-label={lg.group + ' ' + findLine.code}
								>
									<LinePicto
										line={findLine}
										severityDisruption={
											disruption?.severity.level
										}
										aria-hidden="true"
									/>
								</Link>
							)
						} else return null
					})}
				</GridContainer>
			))}
		</section>
	)
}

function returnError(error: string | undefined) {
	return (
		<BlockContainer className={styles.padding}>
			<ErrorComponent errorId={error} />
		</BlockContainer>
	)
}

export function FilteredLinesGroups({
	coord,
	insee,
}: {
	coord?: string
	insee?: string
}) {
	const center: LatLng | undefined = coord
		? [+coord.split(';')[0], +coord.split(';')[1]]
		: undefined

	const [loading, setLoading] = useState<string | null>(null)
	const { linesGroups } = useDataLinesGroups()

	const { stops } = useDataAllStops({ level: 1, disable: !coord })

	const {
		geojson: boundaryGeojson,
		status: statusBoundary,
		error: errorBoundary,
	} = useBoundary(insee, { level: 0 })

	const {
		areas,
		status: statusAreas,
		error: errorAreas,
	} = useDataAllAreas({
		level: 0,
		disable: !coord,
	})
	const { status: statusLines, error: errorLines } = useLoadLines({
		level: 0,
	})
	const { pois: cities } = useDataPoisByType('urban-zones', {
		level: 2,
		disable: !center,
	})

	const centerInUrbanZones = useMemo(() => {
		if (!center) return false
		if (!cities[0]) return false
		return booleanPointInPolygon(
			latlngToPoint(center),
			cities[0].geojson as MultiPolygon,
		)
	}, [cities, center])

	const { geojson } = useBuffer(center, centerInUrbanZones, {
		level: 0,
		disable: !center,
	})

	const objects = useObjectsInGeojson(
		Object.values(areas),
		coord ? geojson : boundaryGeojson,
	)

	const lines = useLinesThroughAreas(objects)

	const mobile = useMobile()
	const { disruptions } = useDataDisruptions({ interval: 600000 })
	const translator = useTranslator()

	if (errorAreas && statusAreas === 'failed') {
		returnError(errorAreas)
	}
	if (errorLines && statusLines === 'failed') {
		returnError(errorLines)
	}
	if (errorBoundary && statusBoundary === 'failed') {
		returnError(errorBoundary)
	}

	if (
		statusLines !== 'loaded' ||
		(insee && statusBoundary !== 'loaded') ||
		statusAreas !== 'loaded'
	) {
		return (
			<BlockContainer className={styles.padding}>
				<Loading />
			</BlockContainer>
		)
	}

	if (!lines.length)
		return (
			<BlockContainer className={styles.padding}>
				<Translation id="around:no-lines" />
			</BlockContainer>
		)

	return (
		<section className={styles.containerLines}>
			<p className={styles.title}>
				{translator.translate('around:lines-around:title')}
			</p>
			{/* //! @TODO: Changer ca avec db et hidden true */}
			{linesGroups?.map(({ group, lines: linesGroup }) => {
				const findLines = lines.filter(lineId =>
					linesGroup.includes(lineId),
				)
				if (findLines.length)
					return (
						<GridContainer
							key={group}
							title={{ title: group, color: 'black' }}
							columns={mobile ? 3 : 4}
							innerClassName={styles.listLines}
						>
							{findLines.map(lineId => {
								return (
									<LineItem
										key={lineId}
										id={lineId}
										disruptions={disruptions}
										stops={stops}
										setLoading={setLoading}
										center={center}
									/>
								)
							})}
						</GridContainer>
					)
			})}
			{loading && <Loading />}
		</section>
	)
}

export function LineItem({
	id,
	disruptions,
	setLoading,
	stops,
	center,
}: {
	id: string
	disruptions: Disruption[]
	setLoading: (loading: string) => void
	stops?: Record<string, Stop>
	center?: LatLng
}) {
	const line = useSelector(state => getLine(state, id))
	useLoadFullLine(id, { level: 1 })
	const [, setLocation] = useLocation()
	const store = useStore()
	const executor = useExecutor()
	const dispatch = useDispatch()
	const module = useModule()

	if (!line) return null

	const filteredDisrutions = disruptions.filter(disruption =>
		disruption.impacted_objects.some(object => object.id === line.id),
	)
	let disruption = filteredDisrutions.find(
		filteredDis => filteredDis.severity?.level === 'blocking',
	)
	if (!disruption) {
		disruption = filteredDisrutions.find(
			filteredDis => filteredDis.severity?.level === 'warning',
		)
	}

	if (!disruption) {
		disruption = filteredDisrutions.find(
			filteredDis => filteredDis.severity?.level === 'info',
		)
	}

	const href = useMemo(() => {
		if (!line) return null
		if (!('routes' in line)) return null
		if (!stops) return null

		const stopsForLine = Object.values(stops).filter(stop =>
			line.routes.some(route => route.stopsOrder.includes(stop.id)),
		)

		let nearestStop: Stop | undefined
		if (center && stopsForLine) {
			for (const stop of stopsForLine) {
				if (!nearestStop) {
					nearestStop = stop
					continue
				}

				const d1 = distance(
					{ type: 'Point', coordinates: center.toReversed() },
					stop.geojson as Point,
				)
				const d2 = distance(
					{ type: 'Point', coordinates: center.toReversed() },
					nearestStop.geojson as Point,
				)

				if (d1 < d2) {
					nearestStop = stop
				}
			}
		}

		const route = line.routes.find(route => {
			if (nearestStop) {
				return route.stopsOrder.includes(nearestStop.id)
			}
			return true
		})
		if (!route) throw new Error('line has no matching route')

		return nearestStop
			? `${module.path}/${line.id}/${route.direction}/${nearestStop.id}`
			: `${module.path}/${line.id}/${route.direction}`
	}, [stops, line, center])

	const hrefObserve = useObserve(href)
	// eslint-disable-next-line @typescript-eslint/no-misused-promises
	const onClick = useCallback<() => void>(async () => {
		let href = hrefObserve.current
		if (!href) {
			setLoading(id)
			void loadStops({ store, executor, level: 0 })
			void loadFullLine({ store, executor, level: 0, line: id })
			href = await hrefObserve.wait()
			if (!href) throw new Error("couldn't find link for line")
		}
		setLocation(href)
	}, [])

	if (!href)
		return (
			<span
				className={styles.lineItem}
				aria-label={line.code}
				onMouseEnter={() => dispatch(setHoveredLine(line.id))}
				onMouseLeave={() => {
					dispatch(setHoveredLine(null))
				}}
				onClick={onClick}
				tabIndex={0}
				onKeyDown={e => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault()
						onClick()
					}
				}}
				role="link"
			>
				<LinePicto
					line={line}
					severityDisruption={disruption?.severity.level}
					aria-hidden="true"
				/>
			</span>
		)

	return (
		<Link
			className={styles.lineItem}
			href={href}
			aria-label={line.code}
			onMouseEnter={() => dispatch(setHoveredLine(line.id))}
			onMouseLeave={() => {
				dispatch(setHoveredLine(null))
			}}
		>
			<LinePicto
				line={line}
				severityDisruption={disruption?.severity.level}
				aria-hidden="true"
			/>
		</Link>
	)
}
